import React from 'react';
import { Routes, Route, Link, NavLink, useLocation, Navigate, useNavigate } from 'react-router-dom';
import { getToken, getTester, clearAuth } from './lib/api.js';
import Login from './pages/Login.jsx';
import Apps from './pages/Apps.jsx';
import AppDetail from './pages/AppDetail.jsx';
import Devices from './pages/Devices.jsx';
import Feedback from './pages/Feedback.jsx';
import FixProgress from './pages/FixProgress.jsx';
// v161.22.24: structured bug-report intake pages
import ReportsList from './pages/ReportsList.jsx';
import NewReport from './pages/NewReport.jsx';
import ReportEditor from './pages/ReportEditor.jsx';
import ReportDetail from './pages/ReportDetail.jsx';
// v161.22.29: PDF upload alternative to the structured form
import UploadPdfReport from './pages/UploadPdfReport.jsx';

// v160.4.49: Admin section fully migrated to dashboard.velkra.app.
// All admin pages (Testers, Groups, Builds, Devices, Feedback, Crashes,
// Storage, Audit, Reliability, FixQueue, AutoFixSettings, ABTest, Compare)
// now live in the dashboard's Distribution section. The old admin pages on
// dist.velkra.app are gone — any direct /admin/* URLs land on AdminMoved
// which redirects to the dashboard.
//
// Why this matters architecturally:
//   • Tester JWTs (issued by SIWA flow here) don't carry admin permissions —
//     the backend's requireAdmin middleware only honors dashboard-issued tokens.
//     So admin pages on this site never actually worked for testers; they
//     just 403'd and confused people.
//   • Keeping admin functionality here meant maintaining two parallel UIs.
//   • The dashboard is where every other admin tool lives.
//
// The old admin .jsx files in src/pages/admin/ and AdminNav.jsx have been deleted.
// Anyone hitting /admin/* on dist.velkra.app gets the AdminMoved redirect below.

function RequireAuth({ children }) {
  const token = getToken();
  if (!token) return <Navigate to="/login" replace />;
  return children;
}

function AdminMoved() {
  const navigate = useNavigate();
  React.useEffect(() => {
    const t = setTimeout(() => {
      window.location.href = 'https://dashboard.velkra.app';
    }, 4500);
    return () => clearTimeout(t);
  }, []);
  return (
    <div style={{ padding: 40, textAlign: 'center', maxWidth: 480, margin: '40px auto' }}>
      <div style={{ fontSize: 48, marginBottom: 16 }}>📦 → 🎛️</div>
      <h1 style={{ fontSize: 22, marginBottom: 12 }}>Admin moved</h1>
      <p style={{ color: 'var(--text-muted, #666)', lineHeight: 1.6, marginBottom: 24 }}>
        Distribution admin (testers, groups, builds, devices, feedback, crashes, storage, audit, reliability, A/B tests, compare, auto-fix) now lives in the
        Dashboard. Redirecting in a few seconds…
      </p>
      <a href="https://dashboard.velkra.app"
         style={{ display: 'inline-block', padding: '10px 20px', background: '#6c5ce7', color: 'white', borderRadius: 8, textDecoration: 'none', fontWeight: 600 }}>
        Open Dashboard now →
      </a>
      <p style={{ marginTop: 24 }}>
        <button onClick={() => navigate('/')}
                style={{ background: 'none', border: 'none', color: 'var(--text-muted, #888)', cursor: 'pointer', fontSize: 13 }}>
          ← Back to apps
        </button>
      </p>
    </div>
  );
}

function Nav() {
  const tester = getTester();
  const loc = useLocation();
  if (loc.pathname === '/login') return null;
  if (!tester) return null;
  return (
    <nav className="nav">
      <Link to="/" className="brand">
        <span className="logo" />
        Velkra Dist
      </Link>
      <div className="links">
        <button onClick={() => { clearAuth(); window.location.href = '/login'; }}>Sign out</button>
      </div>
    </nav>
  );
}

function TabBar() {
  const tester = getTester();
  const loc = useLocation();
  if (!tester || loc.pathname === '/login' || loc.pathname.startsWith('/admin')) return null;
  return (
    <nav className="tab-bar">
      <NavLink to="/" end><span className="ico">📦</span>Apps</NavLink>
      <NavLink to="/devices"><span className="ico">📱</span>Devices</NavLink>
      <NavLink to="/feedback"><span className="ico">💬</span>Feedback</NavLink>
      <NavLink to="/reports"><span className="ico">🐛</span>Reports</NavLink>
    </nav>
  );
}

export default function App() {
  return (
    <div>
      <Nav />
      <div className="app-shell">
        <Routes>
          <Route path="/login" element={<Login />} />

          <Route path="/" element={<RequireAuth><Apps /></RequireAuth>} />
          <Route path="/apps/:slug" element={<RequireAuth><AppDetail /></RequireAuth>} />
          <Route path="/devices" element={<RequireAuth><Devices /></RequireAuth>} />
          <Route path="/feedback" element={<RequireAuth><Feedback /></RequireAuth>} />
          <Route path="/fix-progress/:requestId" element={<RequireAuth><FixProgress /></RequireAuth>} />

          {/* v161.22.24: structured bug-report intake */}
          <Route path="/reports" element={<RequireAuth><ReportsList /></RequireAuth>} />
          <Route path="/reports/new" element={<RequireAuth><NewReport /></RequireAuth>} />
          <Route path="/reports/upload-pdf" element={<RequireAuth><UploadPdfReport /></RequireAuth>} />
          <Route path="/reports/:id/edit" element={<RequireAuth><ReportEditor /></RequireAuth>} />
          <Route path="/reports/:id" element={<RequireAuth><ReportDetail /></RequireAuth>} />

          {/* All /admin/* routes redirect to the dashboard via the AdminMoved page */}
          <Route path="/admin/*" element={<AdminMoved />} />
        </Routes>
      </div>
      <TabBar />
    </div>
  );
}
